import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { fetchOfficeServiceById, updateOfficeService } from "./supportContactService";
import "../assets/css/AddSupportContact.css";

function AddSupportContact({ onBack, onAddContact, initialContactType }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const typeFromUrl = searchParams.get("type");
  const defaultType = initialContactType || (typeFromUrl?.toLowerCase() === "staff" ? "Staff" : "Service");

  const [formData, setFormData] = useState({
    contactType: defaultType,
    serviceName: "",
    category: "",
    primaryContactName: "",
    primaryContactPhone: "",
    supportPhone: "",
    tollFreeNumber: "",
    supportEmail: "",
    supportHours: "",
    supportDays: "",
    status: "Active",
    notes: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [isOfficeService, setIsOfficeService] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const fetchContact = async () => {
      setLoading(true);
      try {
        // Try office-service first (Service tab uses this API)
        let c = await fetchOfficeServiceById(id);
        if (c) {
          setIsOfficeService(true);
        } else {
          // Fallback to support-contact (Staff tab)
          try {
            const response = await axiosInstance.get(`/support-contact/${id}`);
            const raw = response?.data?.data ?? response?.data;
            c = raw?.Support_Contact_Id ? raw : (raw?.support_contact || raw);
            if (c) setIsOfficeService(false);
          } catch (fallbackErr) {
            console.error("Fallback fetch support contact:", fallbackErr);
          }
        }
        if (c) {
          setFormData({
            contactType: c?.Contact_Type ?? c?.contact_type ?? "Service",
            serviceName: c?.Service_Name ?? c?.service_name ?? c?.name ?? "",
            category: c?.Service_Category ?? c?.service_category ?? c?.Category ?? c?.category ?? "",
            primaryContactName: c?.Contact_Name ?? c?.contact_name ?? c?.Primary_Contact_Name ?? c?.primary_contact_name ?? "",
            primaryContactPhone: c?.Contact_Phone ?? c?.contact_phone ?? c?.Primary_Contact_Phone ?? c?.primary_contact_phone ?? "",
            supportPhone: c?.Support_Phone ?? c?.support_phone ?? c?.Contact_Phone ?? c?.contact_phone ?? "",
            tollFreeNumber: c?.Toll_Free_Number ?? c?.toll_free_number ?? "",
            supportEmail: c?.Support_Email ?? c?.support_email ?? "",
            supportHours: c?.Support_Hours ?? c?.support_hours ?? "",
            supportDays: c?.Support_Days ?? c?.support_days ?? "",
            status: c?.Status ?? c?.status ?? "Active",
            notes: c?.Notes ?? c?.notes ?? ""
          });
        } else {
          alert("Contact not found. Please try again.");
        }
      } catch (err) {
        console.error("Error fetching support contact:", err);
        alert("Failed to load contact. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const getCreatedBy = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.user_id ?? user.id ?? 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    return 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.serviceName?.trim() || !formData.category?.trim()) {
      alert("Please fill in all required fields (*)");
      return;
    }

    const payload = {
      Contact_Type: formData.contactType || "Service",
      Service_Name: formData.serviceName.trim(),
      Category: formData.category.trim(),
      Primary_Contact_Name: formData.primaryContactName || null,
      Primary_Contact_Phone: formData.primaryContactPhone || null,
      Support_Phone: formData.supportPhone || null,
      Toll_Free_Number: formData.tollFreeNumber || null,
      Support_Email: formData.supportEmail || null,
      Support_Hours: formData.supportHours || null,
      Support_Days: formData.supportDays || null,
      Status: formData.status || "Active",
      Notes: formData.notes || null
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        payload.Updated_By = getCreatedBy();
        if (isOfficeService) {
          const officePayload = {
            Service_Name: payload.Service_Name,
            Service_Category: payload.Category,
            Contact_Name: payload.Primary_Contact_Name,
            Contact_Phone: payload.Primary_Contact_Phone,
            Alt_Phone: payload.Support_Phone,
            Support_Email: payload.Support_Email,
            Support_Hours: payload.Support_Hours,
            Support_Days: payload.Support_Days,
            Notes: payload.Notes,
            Status: payload.Status,
            Toll_Free_Number: payload.Toll_Free_Number,
            Updated_By: payload.Updated_By
          };
          await updateOfficeService(id, officePayload);
        } else {
          await axiosInstance.put(`/update-support-contact/${id}`, payload);
        }
        alert("Contact updated successfully");
      } else {
        payload.Created_By = getCreatedBy();
        await axiosInstance.post("/create-support-contact", payload);
        alert("Contact added successfully");
      }
      if (onAddContact) onAddContact(formData.contactType);
      if (onBack) onBack();
    } catch (err) {
      console.error("Error saving contact:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (isEdit ? "Failed to update contact." : "Failed to add contact.");
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="addsupportcontact-container">
        <div className="addsupportcontact-loading">Loading contact...</div>
      </div>
    );
  }

  return (
    <div className="addsupportcontact-container">
      <div className="addsupportcontact-header">
        <div className="addsupportcontact-title-section">
          <button className="addsupportcontact-back-link" onClick={onBack}>
            <FaArrowLeft /> Back to Support Contacts
          </button>
        </div>
      </div>

      <div className="addsupportcontact-form-card">
        <form className="addsupportcontact-form" onSubmit={handleSubmit}>
          <div className="addsupportcontact-form-columns">
            <div className="addsupportcontact-form-column">
              <div className="addsupportcontact-form-group">
                <label>
                  Contact Type <span className="required">*</span>
                </label>
                <select
                  name="contactType"
                  value={formData.contactType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Staff">Staff</option>
                  <option value="Service">Service</option>
                </select>
              </div>

              <div className="addsupportcontact-form-group">
                <label>
                  Service Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="serviceName"
                  value={formData.serviceName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Primary Contact Name</label>
                <input
                  type="text"
                  name="primaryContactName"
                  value={formData.primaryContactName}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Support Phone</label>
                <input
                  type="text"
                  name="supportPhone"
                  value={formData.supportPhone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Support Email</label>
                <input
                  type="email"
                  name="supportEmail"
                  value={formData.supportEmail}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Support Hours</label>
                <input
                  type="text"
                  name="supportHours"
                  value={formData.supportHours}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={5}
                />
              </div>
            </div>

            <div className="addsupportcontact-form-column">
              <div className="addsupportcontact-form-group">
                <label>
                  Category <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Primary Contact Phone</label>
                <input
                  type="text"
                  name="primaryContactPhone"
                  value={formData.primaryContactPhone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Toll-Free Number</label>
                <input
                  type="text"
                  name="tollFreeNumber"
                  value={formData.tollFreeNumber}
                  onChange={handleInputChange}
                />
              </div>

              <div className="addsupportcontact-form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="addsupportcontact-form-group">
                <label>Support Days</label>
                <input
                  type="text"
                  name="supportDays"
                  value={formData.supportDays}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="addsupportcontact-form-actions">
            <button
              type="submit"
              className="btn-add-service"
              disabled={submitting}
            >
              {submitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update Contact" : "Add Contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSupportContact;
